import { CreditCard, Info, Loader2 } from "lucide-react-native";
import { Pressable, View, ScrollView } from "react-native";
import StyledText from "../../components/common/ui/styled-text";
import { useSettingsData } from "../../contexts/SettingsDataContext";
import {
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import { usePaymentsService } from "../../services/api/payments";
import { useState } from "react";
import { useStripeClientSecret } from "../../contexts/StripeClientSecretContext";
import LoadingView from "../../components/common/loading-view";

const SetupTab = () => {
  const { createAccount } = usePaymentsService();

  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [errorCreatingAccount, setErrorCreatingAccount] = useState<
    string | null
  >(null);

  const { loadSettings, isLoading, stripeAccountStatus } = useSettingsData();
  const { clientSecret } = useStripeClientSecret();

  const handleSetUpAccount = async () => {
    setIsCreatingAccount(true);
    setErrorCreatingAccount(null);
    try {
      await createAccount();
      await loadSettings();
    } catch (error) {
      console.error(error);
      setErrorCreatingAccount(error as string);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleFinishOnboarding = async () => {
    await loadSettings();
  };

  if (isLoading) {
    return <LoadingView backgroundColor="bg-[#f2f2f2]" />;
  }

  if (stripeAccountStatus !== "NONE") {
    if (!clientSecret) {
      return (
        <div className="p-12 text-center bg-white border-b border-border m-4 shadow-sm rounded-lg w-full max-w-2xl mx-auto">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-700 [transform-origin:center]" />
            <StyledText className="ml-2 text-muted-foreground">
              Loading payment setup...
            </StyledText>
          </div>
        </div>
      );
    }

    return (
      <ScrollView
        className="flex-1 bg-gray-50"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <div className="w-full max-w-2xl mx-auto space-y-4 sm:space-y-6 p-2 sm:p-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <StyledText className="text-xl font-bold text-foreground">
                Onboarding
              </StyledText>
            </div>
            <div className="text-center rounded-md bg-white p-4 border border-border">
              <StyledText className="text-semibold text-gray-700">
                Please continue through the form below to finish your setup and
                start accepting payments.
              </StyledText>
            </div>
          </div>
          <div className="p-2 sm:p-12 text-center bg-white border-border border rounded-md">
            <ConnectAccountOnboarding onExit={handleFinishOnboarding} />
          </div>
        </div>
      </ScrollView>
    );
  }

  return (
    <div className="p-12 text-center bg-white border-b border-border m-4 shadow-sm rounded-lg max-w-2xl mx-auto">
      <div className="w-16 h-16 bg-blue-700/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
        <CreditCard className="text-blue-700" size={32} />
      </div>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Set Up Your Earnings Account
          </h2>
          <p className="text-muted-foreground leading-relaxed text-pretty">
            Get your earnings account set up to start accepting payments from
            your customers.
          </p>
        </div>
        <Pressable
          className={`w-full max-w-md mx-auto gap-2 h-12 text-base font-medium bg-blue-700 rounded-lg flex items-center justify-center flex-row ${
            isCreatingAccount ? "opacity-50" : ""
          }`}
          onPress={handleSetUpAccount}
          disabled={isCreatingAccount}
        >
          {isCreatingAccount && (
            <Loader2 className="w-4 h-4 ml-2 text-white animate-spin [transform-origin:center]" />
          )}
          <StyledText className="text-white font-medium">
            {isCreatingAccount ? "Loading..." : "Set Up Account"}
          </StyledText>
        </Pressable>
        {errorCreatingAccount && (
          <View className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <StyledText className="text-sm text-red-700">
              {errorCreatingAccount.toString()}
            </StyledText>
          </View>
        )}
      </div>
      <div className="mt-12 pt-8 border-t border-border">
        <div className="flex items-start gap-3 text-left max-w-md mx-auto">
          <Info className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-foreground mb-1">Need Help?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our support team is here to help you get set up. Contact us if you
              have any questions about the payment process.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupTab;
